/**
 * Per-city local SEO data.
 *
 * Each city page is generated from this dataset, so editing a single entry
 * here propagates everywhere: H1, meta description, JSON-LD, hreflang URLs,
 * sitemap entry, internal links.
 *
 * Slug must stay URL-safe (lowercase, ASCII, hyphenated) — it becomes the
 * /maitrise-oeuvre/<slug> path. Don't reuse a slug already used elsewhere.
 */
export const CITIES = [
  {
    slug: "rochefort",
    name: "Rochefort",
    department: "Charente-Maritime",
    postalCode: "17300",
    h1: "Maître d'œuvre à Rochefort — patrimoine arsenal, ABF, bâti ancien",
    metaTitle: "Maître d'œuvre à Rochefort (17300) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO, maître d'œuvre installé à Rochefort. Réhabilitation patrimoine, secteur ABF, construction neuve et extensions. Devis et estimation en ligne.",
    intro:
      "Notre cabinet est installé à Rochefort depuis 2016, au cœur du secteur historique. Nous pilotons construction, rénovation et extension dans toute l'agglomération, en travaillant régulièrement avec l'Architecte des Bâtiments de France pour les biens situés dans le périmètre Arsenal — un patrimoine maritime XVIIᵉ exceptionnel qui demande une vraie expertise.",
    specifics: [
      {
        title: "Secteur ABF & patrimoine Arsenal",
        text: "Une grande partie du centre-ville de Rochefort est en périmètre de protection. Pierre de Charente, ardoise, lucarnes, lambrequins : chaque détail compte pour obtenir l'aval des Bâtiments de France. Nous montons les dossiers de DP/PC avec les pièces graphiques attendues et accompagnons les échanges avec l'ABF.",
      },
      {
        title: "Bâti ancien & rénovation énergétique",
        text: "Maisons de faubourg, longères, immeubles XIXᵉ : le bâti ancien rochefortais a ses propres règles. Nous privilégions chaux-chanvre, isolants biosourcés et menuiseries respectueuses du caractère du bien, plutôt que des solutions standard qui dégradent à long terme.",
      },
      {
        title: "Plan local d'urbanisme",
        text: "Rochefort applique un PLU récent avec zonages mixtes. Nous gérons la pré-étude réglementaire et les démarches d'autorisation d'urbanisme dès la phase étude de faisabilité, pour éviter les mauvaises surprises avant que vous ne signiez chez le notaire.",
      },
    ],
    featuredProject: {
      slug: "projet-republique",
      teaser:
        "Réhabilitation d'un immeuble République en plein centre-ville — coordination complète avec l'ABF, mise aux normes énergétiques et conservation des éléments patrimoniaux.",
    },
    nearbyProjects: ["cci-rochefort", "maison-faubourg-rochefort", "maison-rochefort"],
    distanceFromBase: "0 km — siège du cabinet",
    coordinates: { lat: 45.9466, lng: -0.9559 },
  },
  {
    slug: "la-rochelle",
    name: "La Rochelle",
    department: "Charente-Maritime",
    postalCode: "17000",
    h1: "Maître d'œuvre à La Rochelle — maisons de maître, ZPPAUP, ABF",
    metaTitle: "Maître d'œuvre à La Rochelle (17) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO accompagne particuliers et professionnels à La Rochelle. Maisons de maître, rénovation patrimoine, contraintes ZPPAUP et ABF du centre historique.",
    intro:
      "La Rochelle, c'est trente kilomètres au nord de notre siège — une distance que nous parcourons toutes les semaines pour suivre des chantiers. Préfecture, ville d'art et d'histoire, son centre est partiellement classé : tout projet patrimonial exige un dialogue méthodique avec les Bâtiments de France et le respect d'un PLU exigeant.",
    specifics: [
      {
        title: "ZPPAUP et secteur sauvegardé",
        text: "Le centre historique de La Rochelle (Vieux-Port, rues à arcades, hôtels particuliers XVIIᵉ-XVIIIᵉ) est sous protection renforcée. Hauteur de faîtage, matériaux de couverture, type d'enduit, percements : tout est réglementé. Nous connaissons les attentes spécifiques de l'UDAP 17 sur ce périmètre.",
      },
      {
        title: "Maisons de maître & hôtels particuliers",
        text: "La Rochelle compte une densité unique de demeures XIXᵉ avec parquets, moulures, cheminées d'origine. Notre approche : restaurer, pas remplacer. Nous identifions les éléments à conserver dès le diagnostic et nous chiffrons précisément les interventions minimales.",
      },
      {
        title: "Architecture maritime contemporaine",
        text: "Hors centre historique, la dynamique architecturale rochelaise est ouverte au contemporain. Extensions cube, surélévations bois, terrasses tropicales : nous savons faire dialoguer écriture moderne et environnement classé.",
      },
    ],
    featuredProject: {
      slug: "villa-la-rochelle",
      teaser:
        "Réhabilitation complète d'une villa de maître à La Rochelle — restauration des éléments XIXᵉ, ouverture sur le jardin, mise aux normes thermiques sans dénaturer l'âme du lieu.",
    },
    nearbyProjects: ["villa-la-rochelle"],
    distanceFromBase: "30 km",
    coordinates: { lat: 46.1603, lng: -1.1511 },
  },
  {
    slug: "saintes",
    name: "Saintes",
    department: "Charente-Maritime",
    postalCode: "17100",
    h1: "Maître d'œuvre à Saintes — pierre, romain, secteur sauvegardé",
    metaTitle: "Maître d'œuvre à Saintes (17100) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO intervient à Saintes pour vos projets de rénovation patrimoniale, construction et extension. Expertise pierre de Saintes, secteur sauvegardé et bâti ancien.",
    intro:
      "Saintes, troisième ville de Charente-Maritime, est l'un des plus beaux ensembles patrimoniaux de la région : amphithéâtre romain, abbaye aux Dames, hôtels particuliers Renaissance. Si nous n'avons pas encore livré de chantier intra-muros à Saintes, nous intervenons régulièrement sur la Saintonge environnante et maîtrisons les contraintes spécifiques à ce territoire.",
    specifics: [
      {
        title: "Pierre de Saintes",
        text: "Calcaire local du Lutétien, blond légèrement rosé, omniprésent dans le bâti historique saintais. Nous travaillons avec des tailleurs de pierre régionaux pour les restaurations et veillons à la cohérence chromatique des extensions contemporaines.",
      },
      {
        title: "Secteur sauvegardé",
        text: "Le périmètre patrimonial de Saintes est étendu. PSMV, ABF, autorisations d'urbanisme spécifiques : nous structurons la pré-étude réglementaire pour vous éviter les blocages administratifs.",
      },
      {
        title: "Bâti rural en Saintonge",
        text: "Au-delà de la ville, la Saintonge regorge de longères, granges, maisons de pays à pierre apparente. Notre approche bâti ancien est directement applicable : enduits chaux, charpentes traditionnelles, performance thermique respectueuse.",
      },
    ],
    featuredProject: null,
    nearbyProjects: ["grange-marsais", "ecomiam-saintes"],
    distanceFromBase: "40 km",
    coordinates: { lat: 45.7484, lng: -0.6336 },
  },
  {
    slug: "surgeres",
    name: "Surgères",
    department: "Charente-Maritime",
    postalCode: "17700",
    h1: "Maître d'œuvre à Surgères — bâti rural, granges, longères",
    metaTitle: "Maître d'œuvre à Surgères (17700) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO accompagne vos projets de rénovation et construction à Surgères et en Aunis. Expertise bâti rural, granges, charpentes anciennes.",
    intro:
      "Surgères, au carrefour de l'Aunis, c'est un territoire de bâti rural authentique : granges en pierre, fermes à cour fermée, longères de pays. Nous y avons signé l'un de nos chantiers les plus emblématiques — la Grange de Marsais — qui résume notre approche : préserver l'âme du bâti tout en répondant aux usages contemporains.",
    specifics: [
      {
        title: "Granges et bâti agricole reconverti",
        text: "Le défi : transformer un volume rural (charpente apparente, ouvertures rares, sol terre battue) en habitat moderne, sans détruire ce qui en fait la beauté. Nous concevons les percements, isolons par l'intérieur sans masquer la pierre, et créons une circulation contemporaine dans des volumes anciens.",
      },
      {
        title: "Charpentes traditionnelles",
        text: "Les charpentes en chêne des granges saintongeaises ont parfois 200 ans. Diagnostic, consolidation, traitement xylophages, parfois remplacement à l'identique : nous travaillons avec des charpentiers locaux qui connaissent ces assemblages.",
      },
      {
        title: "PLU communal",
        text: "Surgères et les communes alentours (Mauzé, Vouhé, Aigrefeuille) appliquent des règles spécifiques sur le changement de destination (agricole → habitation). Nous validons la faisabilité dès l'étude pré-acquisition.",
      },
    ],
    featuredProject: {
      slug: "grange-marsais",
      teaser:
        "Réhabilitation complète de la Grange de Marsais — préservation de la charpente d'origine, percements contemporains, isolation chaux-chanvre. Une transformation respectueuse pour une famille particulière.",
    },
    nearbyProjects: ["grange-marsais"],
    distanceFromBase: "30 km",
    coordinates: { lat: 46.1083, lng: -0.7517 },
  },
  {
    slug: "royan",
    name: "Royan",
    department: "Charente-Maritime",
    postalCode: "17200",
    h1: "Maître d'œuvre à Royan — architecture années 50, balnéaire, zones naturelles",
    metaTitle: "Maître d'œuvre à Royan (17200) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO intervient à Royan et la Côte de Beauté. Architecture années 50, rénovation, extensions en zone naturelle, résidences secondaires.",
    intro:
      "Royan, c'est un patrimoine architectural unique en France : la ville a été reconstruite après 1945 selon les principes du Modernisme balnéaire. Aujourd'hui, ces villas signées Marcel Breuer, Claude Ferret ou Louis Simon font partie du paysage et exigent une maîtrise d'œuvre sensible à cette histoire. Nous intervenons aussi sur les communes alentours, comme Saint-Georges-de-Didonne où nous avons piloté un projet d'extension en zone naturelle protégée.",
    specifics: [
      {
        title: "Modernisme balnéaire des années 50",
        text: "Lignes pures, toits plats ou à faible pente, brise-soleil, parements en brique de couleur : le vocabulaire architectural royannais a sa propre grammaire. Nos extensions et rénovations dialoguent avec ce style sans pastiche.",
      },
      {
        title: "Zones naturelles protégées",
        text: "Le littoral entre Royan et Saint-Georges-de-Didonne comprend des zones N et Nh très réglementées. Construire ou étendre y est possible sous conditions strictes (emprise, hauteur, intégration paysagère). Nous montons des dossiers solides qui résistent à l'instruction.",
      },
      {
        title: "Résidences secondaires & locatif saisonnier",
        text: "Beaucoup de propriétaires royannais sont à distance. Nous gérons leurs chantiers en autonomie complète, avec un reporting régulier (photos, comptes-rendus écrits) pour qu'ils suivent l'avancement sans déplacement.",
      },
    ],
    featuredProject: {
      slug: "saint-georges-didonne-extension",
      teaser:
        "Extension en zone naturelle protégée à Saint-Georges-de-Didonne — dossier d'urbanisme exigeant, intégration paysagère, écriture contemporaine en dialogue avec le bâti existant.",
    },
    nearbyProjects: ["saint-georges-didonne-extension"],
    distanceFromBase: "70 km",
    coordinates: { lat: 45.6258, lng: -1.0289 },
  },
  {
    slug: "ile-de-re",
    name: "Île de Ré",
    department: "Charente-Maritime",
    postalCode: "17580",
    h1: "Maître d'œuvre à l'Île de Ré — PLU rétais strict, ABF, matériaux locaux",
    metaTitle: "Maître d'œuvre à l'Île de Ré (17) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO intervient à l'Île de Ré pour vos projets de rénovation et construction. PLU rétais strict, ABF, chaux & sable, volets verts, intégration paysagère.",
    intro:
      "L'Île de Ré applique l'un des PLU les plus exigeants de France. Hauteur de faîtage limitée, blanchiment des façades, volets vert rétais, tuile canal : chaque détail est codé pour préserver l'identité de l'île. Nous n'avons pas encore livré de projet sur l'île, mais notre expertise sur les contraintes ABF et patrimoine maritime (acquise à Rochefort et La Rochelle) est directement transposable.",
    specifics: [
      {
        title: "Charte architecturale rétaise",
        text: "Façades blanches lait à la chaux, soubassements gris foncé, volets vert rétais ou bleu marine, tuile canal patinée : la palette est stricte mais offre une vraie cohérence visuelle. Nous l'appliquons rigoureusement, en bonne intelligence avec l'UDAP 17.",
      },
      {
        title: "Loi Littoral & zones submersibles",
        text: "Construire à Ré, c'est composer avec la Loi Littoral, le PPRL (risque submersion marine) et des contraintes d'emprise au sol. Nous gérons les études techniques préalables (cotes de seuil, dispositifs de protection) qui sécurisent le permis de construire.",
      },
      {
        title: "Matériaux & artisans locaux",
        text: "Chaux pour les enduits, sable de Ré, ferronniers de l'île, charpentiers spécialisés : nous travaillons systématiquement avec des entreprises rétaises ou rochelaises qui maîtrisent ces techniques traditionnelles.",
      },
    ],
    featuredProject: null,
    nearbyProjects: ["villa-la-rochelle"],
    distanceFromBase: "45 km",
    coordinates: { lat: 46.1908, lng: -1.4119 },
  },
  {
    slug: "ile-oleron",
    name: "Île d'Oléron",
    department: "Charente-Maritime",
    postalCode: "17310",
    h1: "Maître d'œuvre à l'Île d'Oléron — cabanes ostréicoles, bâti traditionnel",
    metaTitle: "Maître d'œuvre à l'Île d'Oléron (17) — Cabinet ECHO",
    metaDescription:
      "Cabinet ECHO accompagne vos projets de rénovation et construction sur l'Île d'Oléron. Cabanes ostréicoles, bâti vernaculaire, ABF, énergie.",
    intro:
      "L'Île d'Oléron offre une architecture singulière : maisons basses à toit en tuile canal, cabanes ostréicoles colorées, hangars maritimes reconvertibles en habitations. Le territoire mixe protection patrimoniale et opportunités créatives. Nous intervenons sur l'île pour des projets ciblés, en lien avec nos chantiers de Rochefort et de la Saintonge.",
    specifics: [
      {
        title: "Cabanes ostréicoles & bâti maritime",
        text: "Les cabanes des Boyards, Château d'Oléron ou Boyardville sont devenues des lieux iconiques. Les reconvertir en habitation ou bureau demande de respecter la silhouette, les coloris codifiés et les contraintes d'isolation (proximité maritime, vent, embruns).",
      },
      {
        title: "Loi Littoral & zones humides",
        text: "Marais salants, espaces remarquables, sites Natura 2000 : une grande partie de l'île est protégée. Nous identifions très tôt les contraintes et orientons vers des projets viables, plutôt que de partir sur un dossier qui sera bloqué six mois plus tard.",
      },
      {
        title: "Rénovation énergétique en milieu côtier",
        text: "L'air iodé et le vent salin attaquent les matériaux standards. Nos choix techniques (menuiseries aluminium thermo-laquées, enduits respirants, charpentes traitées) sont calibrés pour la durabilité en bord de mer.",
      },
    ],
    featuredProject: null,
    nearbyProjects: ["cci-rochefort", "ecomiam-saintes"],
    distanceFromBase: "50 km",
    coordinates: { lat: 45.9258, lng: -1.3119 },
  },
];

export const getCityBySlug = (slug) => CITIES.find((c) => c.slug === slug);
